import {
  registerDecorator,
  type ValidationArguments,
  type ValidationOptions,
} from 'class-validator';

/** Lunghezze E.164 (senza '+'): minimo pratico, massimo della norma. */
const MIN_DIGITS = 8;
const MAX_DIGITS = 15;
/** Cellulare italiano: 3 + 8 o 9 cifre (10 cifre totali nella forma attuale). */
const IT_MOBILE_REGEX = /^3\d{8,9}$/;

/**
 * Normalizza un cellulare a E.164 (`+393331234567`), o null se non riconducibile
 * a un numero valido. Regole, in ordine:
 * - separatori (spazi, punti, trattini, barre) sempre ignorati;
 * - prefisso internazionale `00` trattato come `+`;
 * - con `+` esplicito il numero è accettato qualunque sia il paese (E.164
 *   puro, non possiamo verificare la "mobilità" senza una libreria di prefix);
 * - senza `+` accettiamo solo l'italiano: 10 cifre che iniziano per 3, oppure
 *   12 cifre `39` + cellulare (paese digitato senza il `+`).
 * Un fisso (0…) o un numero corto viene rifiutato: il campo dichiara un
 * cellulare, meglio il rifiuto esplicito che indovinare il paese.
 */
export function normalizeMobileToE164(value: string): string | null {
  const trimmed = value.trim().replace(/[.\s\-()/]/g, '');
  if (trimmed === '') {
    return null;
  }

  const digits = trimmed.replace(/[^0-9]/g, '');

  if (trimmed.startsWith('+')) {
    return digits.length >= MIN_DIGITS && digits.length <= MAX_DIGITS ? `+${digits}` : null;
  }

  if (trimmed.startsWith('00')) {
    const intl = digits.slice(2);
    return intl.length >= MIN_DIGITS && intl.length <= MAX_DIGITS ? `+${intl}` : null;
  }

  // Nessun '+' e nessun '00': solo numerazione italiana.
  if (IT_MOBILE_REGEX.test(digits)) {
    return `+39${digits}`;
  }
  if (digits.length === 12 && digits.startsWith('39') && IT_MOBILE_REGEX.test(digits.slice(2))) {
    return `+${digits}`;
  }
  return null;
}

/** Due numeri sono lo stesso cellulare se normalizzano allo stesso E.164. */
export function isSameMobile(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalizeMobileToE164(a ?? '');
  const nb = normalizeMobileToE164(b ?? '');
  return na !== null && nb !== null && na === nb;
}

export function isValidMobile(value: unknown): boolean {
  return typeof value === 'string' && normalizeMobileToE164(value) !== null;
}

/**
 * Validates a mobile phone (cellulare). Italian numbers are accepted bare
 * (3…, optionally with the 39 country code typed without '+'), international
 * ones only in explicit E.164 form. Empty/null values are left to @IsOptional.
 */
export function IsMobile(validationOptions?: ValidationOptions) {
  return (object: object, propertyName: string) => {
    registerDecorator({
      name: 'isMobile',
      target: object.constructor,
      propertyName,
      options: validationOptions,
      validator: {
        validate(value: unknown, _args: ValidationArguments) {
          return isValidMobile(value);
        },
        defaultMessage() {
          return 'Inserisci un cellulare valido (es. 333 123 4567)';
        },
      },
    });
  };
}
